import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { custodyHoldingsApi } from '@/services/api';
import { CustodyHoldings, Client } from '@/types/client';
import { useClientApi } from '@/hooks/useClientApi';
import { ArrowLeft } from 'lucide-react';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder, StatGrid } from '@/components/ui';
import { DetailSection, DetailField } from '@/components/utils/PageCard';
import shared from '@/components/utils/shared.module.css';

const CustodyHoldingsDetail: React.FC = () => {
  const { getClient } = useClientApi();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [holdings, setHoldings] = useState<CustodyHoldings[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(!!clientId);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);

  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];

  const loadClientAndHoldings = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);

    try {
      // Load client details and custody holdings in parallel
      const [clientData, response] = await Promise.all([
        getClient(parseInt(clientId)),
        custodyHoldingsApi.getCustodyHoldingsByClient({
          date,
          client_master_id: parseInt(clientId)
        }),
      ]);
      setClient(clientData);

      if (response && response.results) {
        setHoldings(response.results);
      } else {
        console.warn('No results in response:', response);
        setHoldings([]);
      }

      setSummary(response?.summary || null);
    } catch (err) {
      setError('Failed to load custody holdings details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId, date]);

  useEffect(() => {
    if (clientId) {
      loadClientAndHoldings();
    }
  }, [clientId, date, loadClientAndHoldings]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const backButton = (
    <Button onClick={() => navigate('/custody-holdings')} icon={<ArrowLeft size={15} />}>
      Back to Custody Holdings
    </Button>
  );

  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Custody Holdings" />}>
        <Surface>
          <Placeholder kind="loading" message="Loading custody holdings..." />
        </Surface>
      </PageFrame>
    );
  }
  if (error) {
    return (
      <PageFrame header={<PageHeader title="Custody Holdings" actions={backButton} />}>
        <Note tone="error">{error}</Note>
      </PageFrame>
    );
  }
  if (!client) return null;

  return (
    <PageFrame
      header={<PageHeader title={`Custody Holdings - ${client.client_code}`} actions={backButton} />}
    >
      <Surface padded>
        <DetailSection title="Client Information">
          <DetailField label="Name:" value={client.full_name || `${client.first_name} ${client.last_name}`} />
          <DetailField label="Date:" value={new Date(date).toLocaleDateString('en-IN')} />
          <DetailField label="Scheme:" value={client.scheme?.name || client.scheme_name || 'N/A'} />
        </DetailSection>
      </Surface>

      {summary && (
        <Surface title="Holdings Summary" padded>
          <StatGrid
            items={[
              { label: 'Total Holdings:', value: summary.holdings_count || 0 },
              { label: 'Total Saleable Qty:', value: formatNumber(summary.total_saleable_qty || 0) },
              { label: 'Total Logical Qty:', value: formatNumber(summary.total_logical_qty || 0) },
            ]}
          />
        </Surface>
      )}

      <Surface title="Holdings Details">
        <div className={shared['table-scroll']}>
          <table className={shared.table}>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Company Name</th>
                <th className={shared['text-right']}>Saleable Qty</th>
                <th className={shared['text-right']}>Logical Qty</th>
              </tr>
            </thead>
            <tbody>
              {holdings && holdings.length > 0 ? holdings.map((holding) => (
                <tr key={holding.id}>
                  <td>{holding.company_symbol}</td>
                  <td>{holding.company_name}</td>
                  <td className={shared['text-right']}>{formatNumber(holding.adj_saleable_qty || 0)}</td>
                  <td className={shared['text-right']}>{formatNumber(holding.adj_logical_qty || 0)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4}>
                    <Placeholder message="No custody holdings found for this client on the selected date." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Surface>
    </PageFrame>
  );
};

export default CustodyHoldingsDetail;